import { AxiosInstance } from "axios";
import { decode } from "@msgpack/msgpack";
import pako from "pako";

/**
 * Fetch and decode binary msgpack response compressed with pako ungzip
 */
export async function fetchAndDecode(
  axios: AxiosInstance,
  url: string,
  params: any,
  signal?: AbortSignal
): Promise<any> {
  try {
    if (signal?.aborted) {
      throw new Error("Settlement query aborted");
    }

    const res = await axios.get(url, {
      params,
      headers: { Accept: "application/msgpack" },
      responseType: "arraybuffer",
      signal,
    });
    if (!res || !res.data) return {};
    const binary = new Uint8Array(res.data);
    const decompressed = pako.ungzip(binary);
    return decode(decompressed) || {};
  } catch (e) {
    if (!signal?.aborted) {
      console.error(`fetchAndDecode error for ${url}:`, e);
    }
    throw e;
  }
}

export async function fetchOrdersByDateRange(
  axios: AxiosInstance,
  start_date: string,
  end_date: string,
  signal?: AbortSignal,
  embed = "items,return,cancellation,exchange"
): Promise<any[]> {
  const res = await fetchAndDecode(
    axios,
    "/query/orders",
    {
      start_date,
      end_date,
      embed,
    },
    signal
  );
  return (res && res.orders) || [];
}

export async function fetchRefundsByDateRange(
  axios: AxiosInstance,
  start_date: string,
  end_date: string,
  signal?: AbortSignal
): Promise<any[]> {
  const res = await fetchAndDecode(
    axios,
    "/query/refunds",
    {
      start_date,
      end_date,
    },
    signal
  );
  return (res && res.refunds) || [];
}

export async function fetchOrdersByIdsChunked(
  axios: AxiosInstance,
  orderIds: string[],
  chunkSize = 50,
  delayMs = 600,
  signal?: AbortSignal,
  embed = "items,return,cancellation,exchange"
): Promise<any[]> {
  const fetchedOrders: any[] = [];
  const uniqueOrderIds = Array.from(new Set(orderIds.filter(Boolean)));
  for (let i = 0; i < uniqueOrderIds.length; i += chunkSize) {
    if (signal?.aborted) {
      throw new Error("Settlement query aborted");
    }

    const chunk = uniqueOrderIds.slice(i, i + chunkSize);
    const res = await fetchAndDecode(
      axios,
      "/query/orders/by-ids",
      {
        order_ids: chunk.join(","),
        limit: chunk.length,
        offset: 0,
        embed,
      },
      signal
    );

    if (res && res.orders) {
      fetchedOrders.push(...res.orders);
    }
    if (i + chunkSize < uniqueOrderIds.length) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return fetchedOrders;
}

export async function fetchCancelsByDateRange(
  axios: AxiosInstance,
  start_date: string,
  end_date: string,
  date_type = "cancel_complete_date",
  signal?: AbortSignal
): Promise<any[]> {
  const res = await fetchAndDecode(
    axios,
    "/query/cancels",
    {
      start_date,
      end_date,
      date_type,
    },
    signal
  );
  return (res && (res.cancels || res.orders)) || [];
}

export async function fetchExchangesByDateRange(
  axios: AxiosInstance,
  start_date: string,
  end_date: string,
  date_type = "exchange_complete_date",
  signal?: AbortSignal
): Promise<any[]> {
  const res = await fetchAndDecode(
    axios,
    "/query/exchanges",
    {
      start_date,
      end_date,
      date_type,
    },
    signal
  );
  return (res && (res.exchanges || res.orders)) || [];
}

export async function fetchReturnsByDateRange(
  axios: AxiosInstance,
  start_date: string,
  end_date: string,
  date_type = "return_complete_date",
  signal?: AbortSignal
): Promise<any[]> {
  const res = await fetchAndDecode(
    axios,
    "/query/returns",
    {
      start_date,
      end_date,
      date_type,
    },
    signal
  );
  return (res && (res.returns || res.orders)) || [];
}
